import { useCallback, useEffect, useRef, useState } from "react";

import { IPetItemResponse } from "../services/pet-list/pet-list.interface";
import { IPrequalificationInfoResponse } from "../services/prequalification";
import { MIXPANEL_EVENTS } from "../config/mixPanelEvents";
import { PAGES_PATH } from "../navigation/params";
import { isWellnessMember } from "../utils/wellness";
import { useCombinedAlertsDataStore } from "./redux-hooks/combined-alerts-data-store/combined-alerts-data-store";
import { useModal } from "../context/ModalContext";
import { useNavigate } from "react-router-dom";
import { usePetStore } from "./redux-hooks/pet-store/pet-store.service";
import { useWellnessFunnel, isValidBirthdate } from "./useWellnessFunnel";
import useWellnessPlans from "./useWellnessPlans";
import useWellnessPurchaseData from "./redux-hooks/wellness-purchase-store/wellness-purchase-store";
import { userActivityLogger } from "../utils/mixpanel";

interface IUsePrequalContinuationOptions {
  source: string;
}

interface IPendingContinuation {
  record: IPrequalificationInfoResponse;
  pet: IPetItemResponse;
}

export interface UsePrequalContinuationResult {
  continueFromRecord: (
    record: IPrequalificationInfoResponse,
    fallbackPet?: IPetItemResponse,
  ) => void;
  /** True while the direct-to-payment branch waits on the catalog. */
  continuing: boolean;
}

const billingCycleForRecord = (
  record: IPrequalificationInfoResponse,
): "perYear" | "perMonth" => {
  if (record.status === "INELIGIBLE") return "perYear";
  // PRD: approved customers default to monthly costs.
  return record.selectedBilling === "ANNUAL" ? "perYear" : "perMonth";
};

/**
 * Shared purchase-continuation routing for every prequalification entry point
 * (result screen CTA, dashboard continuation card, SMS deep link — SGPD-475).
 *
 * Routes a persisted prequalification record into the existing purchase flow:
 * no saved plan → Plan Selection (via the wellness funnel, keeping its
 * delinquency and birthdate gates); saved plan → straight to Billing Options
 * with the plan rehydrated into Redux first, so the payment page never sees
 * missing purchase data.
 *
 * @param {IUsePrequalContinuationOptions} options Analytics source tag.
 * @return {UsePrequalContinuationResult} `continueFromRecord` + progress flag.
 */
export const usePrequalContinuation = (
  options: IUsePrequalContinuationOptions,
): UsePrequalContinuationResult => {
  const { source } = options;
  const navigate = useNavigate();
  const { petList } = usePetStore();
  const { endsSoonWPData } = useCombinedAlertsDataStore();
  const { setWellnessSelectedPetId } = useModal();
  const { selectPet, blockedByDelinquency } = useWellnessFunnel({ source });
  const { setWellnessPurchaseData } = useWellnessPurchaseData();
  const {
    fetchCatalogItems,
    catalogItems,
    loading: catalogLoading,
    error: catalogError,
    called: catalogCalled,
    getPlanDataByPlanId,
  } = useWellnessPlans();

  const [pending, setPending] = useState<IPendingContinuation | null>(null);
  // getPlanDataByPlanId is recreated per render; keep the latest without
  // retriggering the completion effect below.
  const getPlanDataRef = useRef(getPlanDataByPlanId);
  getPlanDataRef.current = getPlanDataByPlanId;

  const continueFromRecord = useCallback(
    (record: IPrequalificationInfoResponse, fallbackPet?: IPetItemResponse) => {
      const pet = petList?.find((petItem) => petItem.id === record.petId) ?? fallbackPet;
      const hasResult = record.status === "ELIGIBLE" || record.status === "INELIGIBLE";
      if (!pet || !hasResult) {
        navigate(PAGES_PATH.DASHBOARD, { replace: true });
        return;
      }

      // Renewal cohort: the pet still holds its (expiring) plan, so the
      // "already enrolled" short-circuit below must not apply — the renewal
      // funnel continues to Plan Selection, where WellnessPlanPaymentContainer
      // saves a renewal preference instead of a fresh purchase.
      const isRenewalPet = (endsSoonWPData ?? []).some(
        (renewalItem) => renewalItem.petId === pet.id,
      );

      // "Purchase completed" heuristic: the record's pet already has a plan.
      // TODO(SGPD-475): replace with a backend purchaseCompleted signal once
      // the fulfillment model lands (SGPD-482/507).
      if (
        !isRenewalPet &&
        (pet.isWellnessplan || isWellnessMember(petList ?? [], pet.id, true))
      ) {
        navigate(`${PAGES_PATH.PET_CARE}?id=${pet.id}&category=TotalCare_Plan`);
        return;
      }

      if (!record.selectedPlanId) {
        if (isRenewalPet) {
          // selectPet would bounce an enrolled (renewing) pet to PET_CARE, so
          // navigate straight to Plan Selection for the renewal instead.
          setWellnessSelectedPetId(pet.id);
          navigate(`${PAGES_PATH.WELLNESS_PLAN_DETAILS}?id=${pet.id}`);
          return;
        }
        // Plan Selection path — selectPet owns the delinquency + birthdate
        // gates and the funnel-entry analytics.
        selectPet(pet);
        return;
      }

      // Direct-to-Billing-Options path.
      userActivityLogger(MIXPANEL_EVENTS.INTEREST_IN_WELLNESS_PLAN, { referred_from: source });
      if (blockedByDelinquency()) return;
      if (!isValidBirthdate(pet)) {
        // Birthdate modal continues via Plan Selection; acceptable degradation
        // since plan pricing depends on the (still unknown) age tier.
        selectPet(pet);
        return;
      }

      setWellnessSelectedPetId(pet.id);
      fetchCatalogItems(pet.id);
      setPending({ record, pet });
    },
    [
      blockedByDelinquency,
      endsSoonWPData,
      fetchCatalogItems,
      navigate,
      petList,
      selectPet,
      setWellnessSelectedPetId,
      source,
    ],
  );

  // Completes the direct branch once the catalog arrives: rehydrate Redux
  // BEFORE navigating, otherwise the payment page renders PLAN_NOT_FOUND
  // (wellnessPurchaseData is non-persisted).
  useEffect(() => {
    if (!pending) return;

    const fallbackToPlanSelection = () => {
      navigate(`${PAGES_PATH.WELLNESS_PLAN_DETAILS}?id=${pending.pet.id}`);
      setPending(null);
    };

    if (catalogError) {
      fallbackToPlanSelection();
      return;
    }
    if (!catalogCalled || catalogLoading || !catalogItems) return;

    const planData = getPlanDataRef.current(Number(pending.record.selectedPlanId));
    if (!planData) {
      // Saved plan no longer in this pet's catalog — stale selection.
      fallbackToPlanSelection();
      return;
    }

    setWellnessPurchaseData({
      ...planData,
      preferredBillingCycle: billingCycleForRecord(pending.record),
      prequalStatus: pending.record.status === "ELIGIBLE" ? "ELIGIBLE" : "INELIGIBLE",
    });
    navigate(
      `${PAGES_PATH.WELLNESS_PLAN_DETAILS}${PAGES_PATH.WELLNESS_PLAN_CONFIRM_AND_PAY}?id=${pending.pet.id}`,
    );
    setPending(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pending, catalogItems, catalogLoading, catalogError, catalogCalled]);

  return {
    continueFromRecord,
    continuing: pending !== null,
  };
};
